import React, { useEffect, useState } from "react";
import api from "../lib/api";
import USMap from "../components/USMap";
import TerritorySphere from "../components/TerritorySphere";
import DimensionToggle, { useDimension } from "../components/DimensionToggle";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, AlertTriangle, CheckCircle2 } from "lucide-react";

const TIER_PRICE = { bronze: 1000, silver: 2500, gold: 5000 };

export default function TerritoryMap() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [territories, setTerritories] = useState([]);
    const [selected, setSelected] = useState(null);
    const [dimension, , setDimension] = useDimension("3d");

    const load = async () => {
        const { data } = await api.get("/territories");
        setTerritories(data);
    };

    useEffect(() => {
        load();
    }, []);

    const counts = territories.reduce(
        (acc, t) => {
            acc[t.status] = (acc[t.status] || 0) + 1;
            return acc;
        },
        { available: 0, locked: 0, warning: 0 },
    );

    const selectedTerritory = selected ? territories.find((t) => t.state_code === selected.code || t.state_code === selected.state_code) : null;

    return (
        <div className="space-y-8">
            <div className="fade-up">
                <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">The VVS</div>
                <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Territorial Map</h1>
                <p className="text-muted-foreground mt-2 max-w-2xl">
                    Fifty states. Three tiers. Click any territory to inspect status, review pricing,
                    and stake your claim through the Boarding Deck.
                </p>
            </div>

            {/* Status summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 fade-up">
                <SummaryCard label="Available" value={counts.available} color="hsl(40 70% 60%)" icon={CheckCircle2} />
                <SummaryCard label="Locked" value={counts.locked} color="hsl(151 30% 55%)" icon={Lock} />
                <SummaryCard label="Warning" value={counts.warning} color="hsl(350 75% 60%)" icon={AlertTriangle} />
            </div>

            {/* Legend */}
            <div className="glass rounded-xl p-4 flex flex-wrap items-center gap-4 text-xs">
                <LegendDot color="hsl(40 60% 45% / 0.5)" label="Available" />
                <LegendDot color="hsl(151 55% 18%)" label="Locked" />
                <LegendDot color="hsl(350 65% 35%)" label="Warning" />
                <span className="mx-2 text-muted-foreground/50">·</span>
                <TierPill tier="bronze" />
                <TierPill tier="silver" />
                <TierPill tier="gold" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 glass rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                            {dimension === "3d" ? "Drag · scroll · click gold pulses" : "Tactical grid · click a tile"}
                        </div>
                        <DimensionToggle dimension={dimension} onChange={setDimension} />
                    </div>
                    {dimension === "3d" ? (
                        <TerritorySphere
                            territories={territories}
                            selectedCode={selected?.state_code || selected?.code}
                            onSelect={setSelected}
                            height={520}
                        />
                    ) : (
                        <USMap
                            territories={territories}
                            onSelect={setSelected}
                            selectedCode={selected?.state_code || selected?.code}
                        />
                    )}
                </div>

                {/* Detail panel */}
                <div className="glass rounded-2xl p-6 h-fit sticky top-6">
                    {selectedTerritory ? (
                        <TerritoryDetail
                            t={selectedTerritory}
                            canClaim={user?.role === "partner" && selectedTerritory.status === "available"}
                            onClaim={() => navigate(`/app/boarding?state=${selectedTerritory.state_code}`)}
                        />
                    ) : (
                        <div className="text-muted-foreground text-sm">
                            <div className="font-heading text-xl text-foreground mb-2">Select a territory</div>
                            Tap any tile to inspect its tier, status, and boarding fee. Gold states are the crown jewels.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value, color, icon: Icon }) {
    return (
        <div className="glass rounded-xl p-5 flex items-center gap-4" data-testid={`summary-${label.toLowerCase()}`}>
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: color + "33", color }}>
                <Icon size={18} strokeWidth={1.5} />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
                <div className="font-heading text-2xl">{value}</div>
            </div>
        </div>
    );
}

function LegendDot({ color, label }) {
    return (
        <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ background: color }} />
            <span className="text-muted-foreground">{label}</span>
        </div>
    );
}

function TierPill({ tier }) {
    const colors = {
        bronze: "bg-[hsl(25_45%_40%)] text-[hsl(25_30%_90%)]",
        silver: "bg-[hsl(210_5%_50%)] text-[hsl(210_20%_98%)]",
        gold: "bg-[hsl(40_60%_45%)] text-[hsl(40_20%_10%)]",
    };
    return (
        <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full ${colors[tier]}`}>
            {tier} ${TIER_PRICE[tier].toLocaleString()}
        </span>
    );
}

function TerritoryDetail({ t, canClaim, onClaim }) {
    return (
        <div data-testid="territory-detail">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{t.state_code}</div>
            <div className="font-heading text-3xl tracking-tighter">{t.state_name}</div>
            <div className="mt-4 flex items-center gap-2">
                <TierPill tier={t.tier} />
                <StatusPill status={t.status} />
            </div>

            <div className="mt-6 space-y-3 text-sm">
                <Row label="Boarding Fee" value={`$${TIER_PRICE[t.tier].toLocaleString()}`} />
                <Row label="Status" value={t.status} />
                {t.owner_name && <Row label="Operator" value={t.owner_name} />}
                {t.owner_sector && <Row label="Sector" value={t.owner_sector} />}
            </div>

            {canClaim ? (
                <button
                    onClick={onClaim}
                    data-testid="btn-claim-territory"
                    className="mt-6 w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                >
                    Claim {t.state_name}
                </button>
            ) : (
                <div className="mt-6 text-xs text-muted-foreground border-t border-[hsl(40_40%_30%/0.15)] pt-4">
                    {t.status === "locked" ? "This territory has been claimed. Await a reclamation delivery." : "Available for boarding by Silent Partners."}
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const config = {
        available: "bg-[hsl(40_50%_30%/0.4)] text-[hsl(45_85%_75%)]",
        locked: "bg-[hsl(151_55%_18%)] text-[hsl(151_25%_75%)]",
        warning: "bg-[hsl(350_65%_35%)] text-[hsl(0_0%_98%)]",
    };
    return (
        <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full ${config[status]}`}>
            {status}
        </span>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">{label}</span>
            <span className="font-mono text-sm">{value}</span>
        </div>
    );
}
